import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Monitor, Tv, Smartphone, Check, ChevronRight, Loader2, Tag } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_cca8665b-5293-4dd5-89e0-2957c9d7e36c/artifacts/zjtkx4ss_LOGO-MAYAPLUS.png";

function Shell({ children }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen flex flex-col bg-black text-white" style={{ fontFamily: "'Manrope', 'Avenir', Arial, sans-serif", background: 'linear-gradient(180deg, #150a24 0%, #0a0512 55%, #000 100%)' }}>
      <header className="flex items-center justify-between px-6 md:px-14 pt-5 pb-4 border-b border-white/10">
        <img src={LOGO_URL} alt="Maya+" className="h-10 md:h-12 cursor-pointer" onClick={() => navigate('/')} data-testid="signup-logo" />
        {user ? (
          <button onClick={async () => { await logout(); navigate('/'); }} className="text-sm font-semibold hover:underline" data-testid="signup-logout-btn">Se déconnecter</button>
        ) : (
          <button onClick={() => navigate('/auth')} className="text-sm font-semibold hover:underline" data-testid="signup-signin-btn">S'identifier</button>
        )}
      </header>
      <main className="flex-1 flex flex-col items-center px-6 py-14">{children}</main>
      <Footer />
    </div>
  );
}

function StepPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, register } = useAuth();
  const [email, setEmail] = useState((params.get('email') || '').toLowerCase());
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/signup/plans" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { setError('Le mot de passe doit contenir au moins 6 caractères.'); return; }
    setBusy(true);
    setError('');
    try {
      await register(email.toLowerCase().trim(), password, email.split('@')[0]);
      navigate('/signup/plans');
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.includes('already')) {
        navigate(`/verify-code?email=${encodeURIComponent(email)}`);
      } else {
        setError(detail || 'Une erreur est survenue, réessayez.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-full max-w-md" data-testid="signup-step-password">
      <div className="flex items-center gap-4 text-[#E9641A] mb-6">
        <Monitor size={40} strokeWidth={1.5} /><Tv size={48} strokeWidth={1.5} /><Smartphone size={32} strokeWidth={1.5} />
      </div>
      <p className="text-sm text-white/60 mb-1">Étape <b className="text-white">1</b> sur <b className="text-white">3</b></p>
      <h1 className="text-3xl md:text-4xl font-extrabold leading-tight mb-4">Créez un mot de passe pour démarrer votre abonnement</h1>
      <p className="text-white/80 mb-1">Plus que quelques étapes !</p>
      <p className="text-white/80 mb-6">Nous non plus, on n'aime pas la paperasse.</p>
      <form onSubmit={submit} className="flex flex-col gap-3">
        <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="Adresse e-mail"
          data-testid="signup-email-input"
          className="bg-white/5 border border-white/30 rounded-md px-4 py-3.5 text-white placeholder-white/50 focus:outline-none focus:border-[#E9641A] transition-colors" />
        <input type="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder="Mot de passe (6 caractères min.)"
          data-testid="signup-password-input"
          className="bg-white/5 border border-white/30 rounded-md px-4 py-3.5 text-white placeholder-white/50 focus:outline-none focus:border-[#E9641A] transition-colors" />
        {error && <p className="text-sm text-red-400" data-testid="signup-password-error">{error}</p>}
        <button type="submit" disabled={busy} data-testid="signup-password-next-btn"
          className="mt-2 bg-[#E9641A] hover:bg-[#FE975E] disabled:opacity-60 text-white text-xl font-bold py-3.5 rounded-md transition-colors">
          {busy ? 'Création…' : 'Suivant'}
        </button>
      </form>
    </div>
  );
}

function StepPlans() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showPromo, setShowPromo] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/plans`).then(r => {
      const paid = (r.data || []).filter(p => p.price > 0).sort((a, b) => (a.sort_order ?? 99) - (b.sort_order ?? 99));
      setPlans(paid);
      if (paid.length) {
        const premium = paid.find(p => p.id === 'premium') || paid.find(p => p.price === 9.99) || paid[paid.length - 1];
        setSelected(premium.id);
      }
    }).catch(() => {});
  }, []);

  if (loading) return null;
  if (!user) return <Navigate to="/signup" replace />;

  const fmtPrice = (p) => `${String(p.price).replace('.', ',')} €/${p.billing === 'annual' || p.billing === 'yearly' ? 'an' : 'mois'}`;

  const goCheckout = async () => {
    if (!selected) return;
    setBusy(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/subscribe/checkout`, {
        plan_id: selected, promo_code: promoCode.trim(), origin_url: window.location.origin,
      }, { withCredentials: true });
      window.location.href = data.url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Impossible de démarrer le paiement, réessayez.');
      setBusy(false);
    }
  };

  return (
    <div className="w-full max-w-5xl" data-testid="signup-step-plans">
      <p className="text-sm text-white/60 mb-1">Étape <b className="text-white">2</b> sur <b className="text-white">3</b></p>
      <h1 className="text-3xl md:text-4xl font-extrabold leading-tight mb-2">Choisissez le forfait qui vous convient</h1>
      <p className="text-white/70 mb-8">Sans engagement, résiliable à tout moment.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {plans.map(p => {
          const isSel = selected === p.id;
          return (
            <button key={p.id} onClick={() => setSelected(p.id)} data-testid={`signup-plan-${p.id}`}
              className={`relative text-left rounded-xl overflow-hidden border transition-all ${isSel ? 'border-[#E9641A] ring-2 ring-[#E9641A]/60 scale-[1.02]' : 'border-white/15 hover:border-white/40'}`}>
              <div className="px-4 py-4" style={{ background: isSel ? 'linear-gradient(135deg, #E9641A 0%, #8a2be2 130%)' : 'linear-gradient(135deg, #2a1447 0%, #3b1a55 100%)' }}>
                <p className="font-bold text-lg leading-tight">{p.name}</p>
                <p className="text-sm text-white/80">{fmtPrice(p)}</p>
                {isSel && <span className="absolute top-3 right-3 w-6 h-6 rounded-full bg-white flex items-center justify-center"><Check size={15} className="text-[#E9641A]" strokeWidth={3} /></span>}
              </div>
              <div className="px-4 py-4 bg-white/[0.04] space-y-2.5 min-h-[170px]">
                {(p.features || []).map((f, i) => (
                  <p key={i} className="text-[13px] text-white/70 leading-snug flex gap-2"><Check size={14} className="text-[#E9641A] flex-none mt-0.5" />{f}</p>
                ))}
              </div>
            </button>
          );
        })}
      </div>

      <div className="max-w-md">
        {!showPromo ? (
          <button onClick={() => setShowPromo(true)} className="flex items-center gap-2 text-sm text-white/70 underline hover:text-white mb-4" data-testid="signup-promo-toggle">
            <Tag size={15} /> J'ai un code promo
          </button>
        ) : (
          <input value={promoCode} onChange={e => setPromoCode(e.target.value.toUpperCase())} placeholder="Code promo"
            data-testid="signup-promo-input"
            className="w-full bg-white/5 border border-white/30 rounded-md px-4 py-3 mb-4 text-white placeholder-white/50 focus:outline-none focus:border-[#E9641A] transition-colors uppercase" />
        )}
        {error && <p className="text-sm text-red-400 mb-3" data-testid="signup-plans-error">{error}</p>}
        <button onClick={goCheckout} disabled={busy || !selected} data-testid="signup-plans-next-btn"
          className="w-full flex items-center justify-center gap-2 bg-[#E9641A] hover:bg-[#FE975E] disabled:opacity-60 text-white text-xl font-bold py-3.5 rounded-md transition-colors">
          {busy ? <Loader2 size={22} className="animate-spin" /> : <>Suivant <ChevronRight size={22} /></>}
        </button>
        <p className="text-xs text-white/40 mt-3">Étape 3 : paiement sécurisé par Stripe. L'accès est activé dès la confirmation du paiement.</p>
      </div>
    </div>
  );
}

function StepSuccess() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('checking');
  const triesRef = useRef(0);

  useEffect(() => {
    if (!sessionId) { navigate('/signup/plans', { replace: true }); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await axios.get(`${API}/subscribe/status/${sessionId}`, { withCredentials: true });
        if (data.payment_status === 'paid') {
          setState('paid');
          await checkAuth();
          setTimeout(() => navigate('/', { replace: true }), 1800);
          return;
        }
      } catch { /* retry */ }
      triesRef.current += 1;
      if (triesRef.current >= 15) { setState('timeout'); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="w-full max-w-md text-center pt-10" data-testid="signup-step-success">
      {state === 'checking' && (<>
        <Loader2 size={44} className="animate-spin text-[#E9641A] mx-auto mb-6" />
        <h1 className="text-2xl font-extrabold mb-3">Confirmation de votre paiement…</h1>
        <p className="text-white/60">Quelques secondes, nous vérifions votre abonnement auprès de Stripe.</p>
      </>)}
      {state === 'paid' && (<>
        <span className="w-16 h-16 rounded-full bg-[#E9641A]/15 flex items-center justify-center mx-auto mb-6"><Check size={34} className="text-[#E9641A]" strokeWidth={3} /></span>
        <h1 className="text-2xl font-extrabold mb-3" data-testid="signup-success-title">Bienvenue sur Maya+ !</h1>
        <p className="text-white/60">Votre abonnement est actif. Redirection…</p>
      </>)}
      {state === 'timeout' && (<>
        <h1 className="text-2xl font-extrabold mb-3">Paiement en cours de validation</h1>
        <p className="text-white/60 mb-6">La confirmation prend plus de temps que prévu. Réessayez dans un instant.</p>
        <button onClick={() => window.location.reload()} className="bg-[#E9641A] hover:bg-[#FE975E] text-white font-bold px-6 py-3 rounded-md transition-colors">Vérifier à nouveau</button>
      </>)}
    </div>
  );
}

export default function SignupFlow() {
  const { pathname } = useLocation();
  return (
    <Shell>
      {pathname === '/signup' && <StepPassword />}
      {pathname === '/signup/plans' && <StepPlans />}
      {pathname === '/signup/success' && <StepSuccess />}
    </Shell>
  );
}
